import clsx from 'clsx'
import qs from 'qs'
import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useHistory, useParams } from 'react-router'
import { catchError, EMPTY, finalize, from, takeUntil } from 'rxjs'
import { CampaignApi } from 'src/api'
import { axiosHiringApi } from 'src/api/axios'
import {
  BreadcrumbDivider,
  BreadcrumbHeading,
  Checkbox,
  InputDebounce,
  TabsNav
} from 'src/components'
import { ECampaignSubmissionStatus, EJobQuestionType, ETrackingEvent } from 'src/enums'
import {
  useDebounceAnalyticInput,
  useOnClickOutside,
  useQueryParams,
  useUnsubscribe,
  useUnsubscribeEffect
} from 'src/hooks'
import { IconFilter, IconSearch } from 'src/icons'
import { ICampaignModel, ICampaignSubmissionModel } from 'src/interfaces'
import { Breadcrumbs } from 'src/layouts/breadcrumbs'
import { browserHistory, ERoutes, generate } from 'src/router'
import { LoadMoreService, SnackbarService } from 'src/services'
import { LoadingModule } from 'src/store'
import { ECampaignRoutes } from '../../routes.enum'
import { ApplicantItems } from '../applicant-items'
import Style from './style.module.scss'

export const Applicants: FC = () => {
  const history = useHistory()
  const { tab } = useQueryParams()
  const { campaignId } = useParams<{ campaignId: string }>()
  const { analyticInput } = useDebounceAnalyticInput('top_nav')
  const unsubscribe$ = useUnsubscribe()

  const [counts, setCounts] = useState<{
    to_review?: number
    great_match?: number
    possible_match?: number
    not_a_match?: number
    all?: number
    passed?: number
  }>({
    to_review: 0,
    great_match: 0,
    possible_match: 0,
    not_a_match: 0,
    all: 0,
    passed: 0
  })
  const [tabActive, setTabActive] = useState<ECampaignSubmissionStatus>(ECampaignSubmissionStatus.TO_REVIEW)
  const [keyword, setKeyword] = useState<string>()
  const [campaign, setCampaign] = useState<ICampaignModel>()

  const handleSetKeyword = (value?: string) => {
    setKeyword(value)
    analyticInput(ETrackingEvent.INPUT_NAV_SEARCH_APPLICANTS, value)
  }

  const _loadMoreService = useMemo(
    () =>
      new LoadMoreService<ICampaignSubmissionModel>({
        axiosInstance: axiosHiringApi,
        endpoint:
          `${CampaignApi._prefix}/${campaignId}/submission?` +
          qs.stringify({
            keyword,
            limit: 100,
            status: tabActive === ECampaignSubmissionStatus.ALL ? undefined : tabActive
          })
      }),
    [campaignId, keyword, tabActive]
  )

  useUnsubscribeEffect(
    (unsubscribe$) => {
      if (!campaignId) return

      from(CampaignApi.getOne(Number(campaignId)))
        .pipe(
          takeUntil(unsubscribe$),
          catchError((error) => {
            SnackbarService.axiosError(error)
            history.push(generate(ERoutes.ERROR_404))
            return EMPTY
          })
        )
        .subscribe(({ data }) => {
          setCampaign(data)
        })
    },
    [campaignId]
  )

  const countSubmission = useCallback(() => {
    if (!campaignId) return

    LoadingModule.toggle(true)
    from(CampaignApi.countSubmission(Number(campaignId), { keyword }))
      .pipe(
        takeUntil(unsubscribe$),
        catchError((error) => {
          SnackbarService.axiosError(error)
          return EMPTY
        }),
        finalize(() => LoadingModule.toggle(false))
      )
      .subscribe(({ data }) => setCounts(data))
  }, [campaignId, keyword, unsubscribe$])

  useEffect(() => {
    countSubmission()
  }, [countSubmission])

  useEffect(() => {
    setTabActive(tab as ECampaignSubmissionStatus)
  }, [tab])

  const tabLeftItems = useMemo(() => {
    return [
      {
        key: ECampaignSubmissionStatus.TO_REVIEW,
        label: `To Review (${counts?.to_review || 0})`,
        children: (
          <ApplicantItems
            tab={ECampaignSubmissionStatus.TO_REVIEW}
            count={counts?.to_review}
            campaign={campaign}
            countSubmission={countSubmission}
            _loadMoreService={_loadMoreService}
          />
        )
      },
      {
        key: ECampaignSubmissionStatus.GREAT_MATCH,
        label: `Great Match (${counts?.great_match || 0})`,
        children: (
          <ApplicantItems
            tab={ECampaignSubmissionStatus.GREAT_MATCH}
            count={counts?.great_match}
            campaign={campaign}
            countSubmission={countSubmission}
            _loadMoreService={_loadMoreService}
          />
        )
      },
      {
        key: ECampaignSubmissionStatus.POSSIBLE_MATCH,
        label: `Possible Match (${counts?.possible_match || 0})`,
        children: (
          <ApplicantItems
            tab={ECampaignSubmissionStatus.POSSIBLE_MATCH}
            count={counts?.possible_match}
            campaign={campaign}
            countSubmission={countSubmission}
            _loadMoreService={_loadMoreService}
          />
        )
      },
      {
        key: ECampaignSubmissionStatus.NOT_A_MATCH,
        label: `Not a Match (${counts?.not_a_match || 0})`,
        children: (
          <ApplicantItems
            tab={ECampaignSubmissionStatus.NOT_A_MATCH}
            count={counts?.not_a_match}
            campaign={campaign}
            countSubmission={countSubmission}
            _loadMoreService={_loadMoreService}
          />
        )
      }
    ]
  }, [counts, campaign, countSubmission, _loadMoreService])

  const tabRightItems = useMemo(() => {
    return [
      {
        key: ECampaignSubmissionStatus.ALL,
        label: `Everyone (${counts?.all || 0})`,
        children: (
          <ApplicantItems
            tab={ECampaignSubmissionStatus.ALL}
            count={counts?.all}
            campaign={campaign}
            countSubmission={countSubmission}
            _loadMoreService={_loadMoreService}
          />
        )
      },
      {
        key: ECampaignSubmissionStatus.PASSED,
        label: `Passed (${counts?.passed || 0})`,
        children: (
          <ApplicantItems
            tab={ECampaignSubmissionStatus.PASSED}
            count={counts?.passed}
            campaign={campaign}
            countSubmission={countSubmission}
            _loadMoreService={_loadMoreService}
          />
        )
      }
    ]
  }, [counts, campaign, countSubmission, _loadMoreService])

  // Filter
  const [isFilter, setIsFilter] = useState(false)
  const [answerFilters, setAnswerFilters] = useState<{ questionId: number; answers: number[] }[]>([])
  const filterRef = useRef(null)
  useOnClickOutside(filterRef, () => setIsFilter(false))

  const applicationQuestions = useMemo(() => campaign?.applicationQuestions?.filter((question) => question.type !== EJobQuestionType.LINK), [campaign?.applicationQuestions])

  const loadApplicants = useCallback((answerFilters?: { questionId: number; answers: number[] }[]) => {
    _loadMoreService.reset()
    _loadMoreService.loadMore({ answerFilters })
  }, [_loadMoreService])

  const onFilter = (questionId: number, answerId: number) => {
    const isExistQuestionId = answerFilters?.some((item) => item.questionId === questionId)

    if (isExistQuestionId) {
      let filterUpdated = answerFilters?.map((item) => {
        if (item.questionId === questionId) {
          if (item.answers?.includes(answerId)) {
            item.answers = item.answers.filter((answer) => answer !== answerId)
            return item
          }

          item.answers = [...item.answers, answerId]
          return item
        }

        return item
      })

      filterUpdated = filterUpdated.filter((item) => item.answers?.length)
      setAnswerFilters(filterUpdated)
      loadApplicants(filterUpdated)
      return
    }

    const newAnswerFilters = [...answerFilters, { questionId, answers: [answerId] }]
    setAnswerFilters(newAnswerFilters)
    loadApplicants(newAnswerFilters)
  }

  return (
    <>
      <Breadcrumbs>
        <div className="fx fx-ai-center gap-6">
          <div className="fx fx-ai-center gap-3">
            <BreadcrumbHeading
              className="pointer"
              onClick={() => history.push(generate(ECampaignRoutes.CAMPAIGN_MY_JOBS))}
            >
              My Jobs
            </BreadcrumbHeading>
            <BreadcrumbDivider/>
            <BreadcrumbHeading active>{campaign?.jobTitle}</BreadcrumbHeading>
          </div>
          <InputDebounce
            prefix={<IconSearch size={20}/>}
            style={{ width: 350 }}
            placeholder="Search Applicants..."
            value={keyword}
            onChange={handleSetKeyword}
          />
        </div>
      </Breadcrumbs>

      <section className="pt-8">
        <div className="flex fx-jc-space-between fx-ai-center mb-5 px-8">
          <div className="semibold-28 txt-black-01">{campaign?.jobTitle}</div>

          <div className="fx fx-ai-center gap-5">
            {(!!applicationQuestions?.length && !!counts?.[tabActive]) && (
              <div className="relative" ref={filterRef}>
                <div
                  id="filter-btn-guide"
                  className={clsx('fw-500 fx fx-ai-center gap-2 pointer txt-blue-1000 hover-txt-blue-1100 p-1 nowrap', Style.filter, {
                    [Style.filterActive]: isFilter
                  })}
                  onClick={() => setIsFilter(!isFilter)}
                >
                  Filter
                  <IconFilter className="txt-blue-1000 hover-txt-blue-1100"/>
                </div>
                <div className={clsx(Style.filterDropdown, !isFilter && 'd-none')}>
                  <div className="fx fx-ai-center fx-jc-space-between gap-2">
                    <div className="fw-700 txt-black-01">Filter by Question Results</div>
                    <div
                      className="fw-500 txt-blue-1000 pointer"
                      onClick={() => {
                        answerFilters?.length && setAnswerFilters([])
                        answerFilters?.length && loadApplicants()
                      }}
                    >
                      Clear all
                    </div>
                  </div>

                  {applicationQuestions?.map((question) => (
                    <div key={question.id} className="fx-column gap-3">
                      <div className="txt-black-01 fw-500">
                        {question.content}
                      </div>

                      {question.options?.map((option) => (
                        <Checkbox
                          key={option.id}
                          className="mr-4"
                          label={option.content}
                          checked={answerFilters?.some((item) => option.id && item.answers.includes(option.id))}
                          onChange={() => question.id && option.id && onFilter(question.id, option.id)}
                          labelClassName="txt-black-02"
                          checkBoxContentStyle={{ minWidth: 24 }}
                        />
                      ))}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        <TabsNav
          className="fx-1 fx-column gap-8"
          containerProps={{ className: 'mx-8' }}
          defaultActiveKey={tabActive}
          items={tabLeftItems}
          rightItems={tabRightItems}
          onChange={(tab) => browserHistory.push(generate([ECampaignRoutes.CAMPAIGNS_APPLICANTS, { campaignId: campaign?.id }], { tab }))}
        />
      </section>
      {/* {!!items.length && <FirstTimeApplicant isFilter={!!applicationQuestions?.length && !!counts?.[tabActive]}/>} */}
    </>
  )
}
